/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState } from 'react';
import {
    Box,
    Drawer,
    Typography,
    Button,
    Chip,
    IconButton,
    Rating,
    Divider,
    useMediaQuery,
    useTheme,
    SwipeableDrawer,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    Close,
    Hotel,
    Flight,
    DirectionsCar,
    Attractions,
    Security,
    CheckCircle,
    Search,
    ShoppingCart,
    Info,
    ArrowForward,
    AccessTime,
} from '@mui/icons-material';
import { Ad, AdPage, AdPlacement, AdCategory } from '../../data/mockAds';
import { useAds } from '../../contexts/AdContext';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

// ============================================================================
// TYPES
// ============================================================================

interface AdDrawerProps {
    open: boolean;
    onClose: () => void;
    ad: Ad | null;
    page: AdPage;
    placement: AdPlacement;
}

// ============================================================================
// CATEGORY ICONS
// ============================================================================

const categoryIcons: Record<AdCategory, React.ReactElement> = {
    hotel: <Hotel />,
    flight: <Flight />,
    car: <DirectionsCar />,
    activity: <Attractions />,
    insurance: <Security />,
    general: <Attractions />,
};

const actionIcons: Record<string, React.ReactElement> = {
    search: <Search />,
    book: <ShoppingCart />,
    learn_more: <Info />,
};

// ============================================================================
// COMPONENT
// ============================================================================

const AdDrawer: React.FC<AdDrawerProps> = ({
    open,
    onClose,
    ad,
    page,
    placement,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { trackClick } = useAds();
    const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
    const bookingEnabled = !flagsLoading && isEnabled('booking_enabled') === true;
    const [comingSoonMessage, setComingSoonMessage] = useState('');
    
    // Handle CTA action (all inline - never leaves site)
    const handleAction = useCallback(() => {
        if (!ad) return;
        
        trackClick(ad, page, placement, ad.inlineContent.actionType);
        
        // Navigate based on action type - all internal
        const { actionType, actionParams } = ad.inlineContent;
        
        if ((actionType === 'book' || actionType === 'search') && !bookingEnabled) {
            setComingSoonMessage('Booking is coming soon - stay tuned!');
            return;
        }
        switch (actionType) {
            case 'search':
                // Navigate to search page with params
                if (actionParams?.type === 'hotel') {
                    navigate(`/search/hotels?partner=${actionParams.partner || ''}`);
                } else if (actionParams?.type === 'flight') {
                    navigate(`/search/flights?partner=${actionParams.partner || ''}`);
                } else if (actionParams?.type === 'car') {
                    navigate(`/search/cars?partner=${actionParams.partner || ''}`);
                } else if (actionParams?.type === 'activity') {
                    navigate(`/search/activities?partner=${actionParams.partner || ''}&destination=${actionParams.destination || ''}`);
                }
                break;
            case 'book':
                // Navigate to booking page
                navigate('/book');
                break;
            case 'learn_more':
                // Stay in drawer - could expand content or show modal
                // For now, just close
                break;
        }
        
        onClose();
    }, [ad, page, placement, trackClick, navigate, onClose, bookingEnabled]);
    
    if (!ad) return null;
    
    const content = ad.inlineContent;
    const CategoryIcon = categoryIcons[ad.category];
    const ActionIcon = actionIcons[content.actionType];
    
    // Drawer content
    const drawerContent = (
        <Box
            sx={{
                width: isMobile ? '100%' : 420,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#fff',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: '1px solid #e0e0e0',
                    bgcolor: '#E8F5F3',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label="Sponsored"
                        size="small"
                        sx={{
                            bgcolor: '#2D6E6A',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        Partner Content
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </Box>
            
            {/* Main Image */}
            {content.images && content.images.length > 0 && (
                <Box
                    component="img"
                    src={content.images[0]}
                    alt={content.headline}
                    sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                    }}
                />
            )}
            
            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Category */}
                <Chip
                    icon={CategoryIcon}
                    label={ad.category}
                    size="small"
                    sx={{
                        mb: 2,
                        height: 26,
                        textTransform: 'capitalize',
                        bgcolor: '#f0f0f0',
                    }}
                />
                
                {/* Headline */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontFamily: 'Poppins, sans-serif',
                        mb: 1,
                        lineHeight: 1.3,
                    }}
                >
                    {content.headline}
                </Typography>
                
                {/* Partner name */}
                <Typography
                    variant="body2"
                    sx={{ color: '#666', mb: 2 }}
                >
                    by {ad.title}
                </Typography>
                
                {/* Rating */}
                {content.rating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Rating value={content.rating} precision={0.1} readOnly />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {content.rating}
                        </Typography>
                        {content.reviewCount && (
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                ({content.reviewCount.toLocaleString()} reviews)
                            </Typography>
                        )}
                    </Box>
                )}
                
                {/* Body */}
                <Typography
                    variant="body1"
                    sx={{
                        color: '#444',
                        lineHeight: 1.7,
                        mb: 3,
                    }}
                >
                    {content.body}
                </Typography>
                
                {/* Features */}
                {content.features && content.features.length > 0 && (
                    <>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1.5 }}
                        >
                            What's Included
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                            {content.features.map((feature, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <CheckCircle
                                        sx={{ fontSize: 18, color: '#2E7D32' }}
                                    />
                                    <Typography variant="body2">
                                        {feature}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
                
                {/* Price Section */}
                {content.price && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: '#FFF8E1',
                            border: '1px solid #FFE082',
                            mb: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: '#FF6B35' }}
                            >
                                {content.price}
                            </Typography>
                            {content.originalPrice && (
                                <Typography
                                    sx={{
                                        textDecoration: 'line-through',
                                        color: '#999',
                                    }}
                                >
                                    {content.originalPrice}
                                </Typography>
                            )}
                        </Box>
                        {content.discount && (
                            <Chip
                                label={content.discount}
                                size="small"
                                sx={{
                                    bgcolor: '#E65100',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                }}
                            />
                        )}
                    </Box>
                )}
                
                {/* Additional Images */}
                {content.images && content.images.length > 1 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 1.5 }}
                        >
                            Gallery
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                overflowX: 'auto',
                                pb: 1,
                            }}
                        >
                            {content.images.slice(1).map((img, i) => (
                                <Box
                                    key={i}
                                    component="img"
                                    src={img}
                                    alt={`${content.headline} ${i + 2}`}
                                    sx={{
                                        width: 120,
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        flexShrink: 0,
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
            
            {/* Footer - CTA */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: '#fafafa',
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={ActionIcon}
                    endIcon={<ArrowForward />}
                    onClick={handleAction}
                    sx={{
                        bgcolor: '#FF6B35',
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1.5,
                        fontSize: '1rem',
                        boxShadow: '0 4px 12px rgba(255,107,53,0.3)',
                        '&:hover': {
                            bgcolor: '#E55A2B',
                            boxShadow: '0 6px 16px rgba(255,107,53,0.4)',
                        },
                    }}
                >
                    {ad.ctaText}
                </Button>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 1,
                        color: '#999',
                    }}
                >
                    You'll stay on Percoro
                </Typography>
            </Box>
            {/* Coming Soon Snackbar */}
            <Snackbar
                open={!!comingSoonMessage}
                autoHideDuration={4000}
                onClose={() => setComingSoonMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setComingSoonMessage('')}
                    severity="info"
                    sx={{ width: '100%' }}
                >
                    {comingSoonMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
    
    // Use SwipeableDrawer on mobile for better UX
    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                onOpen={() => {}}
                disableSwipeToOpen
                PaperProps={{
                    sx: {
                        maxHeight: '90vh',
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                    },
                }}
            >
                {drawerContent}
            </SwipeableDrawer>
        );
    }
    
    // Use regular Drawer on desktop
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 420,
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default AdDrawer;
